import React, { useState, useEffect, useRef } from "react";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Autocomplete from "@mui/material/Autocomplete";
import Fade from "@mui/material/Fade";

import { captionsState } from "./JotaiAtoms";

import { useAtom } from "jotai";
import { useHttpPostAndProject } from "./hooks";

type CaptionButtonInputProps = {
  storyId: string;
  initialValue: string;
  onSelected?: (value?: string) => void;
};

function CaptionButtonInput({
  storyId,
  initialValue,
  onSelected,
}: CaptionButtonInputProps) {
  const [value, setValue] = useState<string | undefined>(initialValue);
  const ref = useRef<string>("");
  const inputRef = useRef<HTMLInputElement>();
  const [httpPost, project] = useHttpPostAndProject();

  const [captions, setCaptions] = useAtom(captionsState);

  const handleCaption = (value?: string) => {
    const request = async (value: string) => {
      try {
        httpPost("label", {
          setstoryprops: {
            story: storyId,
            st_cap: value,
          },
        });
        if (value !== "") {
          const proj = project;
          setCaptions((prev: { [key: string]: { [key: string]: string } }) => {
            if (proj in prev) {
              let newCaps = { ...captions };
              newCaps[proj] = { ...prev[proj], [value]: storyId };
              return newCaps;
            }
            return prev;
          });
        }
      } catch (err) {
      } finally {
      }
    };

    if (value !== undefined) {
      request(value);
    }
    if (onSelected) {
      onSelected(value);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLElement>) => {
    ref.current = e.key;
    if (e.key === "Enter") {
      e.preventDefault();
      handleCaption(value || "");
    } else if (e.key === "Escape") {
      handleCaption();
    }
  };

  const saveText = (v: string) => {
    if (v) {
      if (v.trimStart().length <= 18) {
        setValue(v.trimStart());
      }
    } else {
      setValue(undefined);
    }
  };

  const handleChange = (e: any, v: string) => {
    saveText(v);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    saveText(e.target.value);
  };

  useEffect(() => {
    if (inputRef?.current) {
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          inputRef.current!.select();
        }
      }, 0);
    }
  }, []);

  const theme = useTheme();

  return (
    <Autocomplete
      value={value || ""}
      freeSolo
      autoHighlight
      autoSelect
      clearOnEscape
      disableClearable
      onChange={handleChange}
      options={captions[project] ? Object.keys(captions[project]) : []}
      size="small"
      sx={{
        width: "180px",
        "& .MuiAutocomplete-inputRoot": {
          padding: "0 !important",
        },
        "& .MuiAutocomplete-endAdornment": {
          display: "none",
        },
      }}
      slotProps={{
        popper: {
          sx: {
            "& .MuiPaper-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1f1f1f" : "#ffffff",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0, 0, 0, 0.5)"
                  : "0 4px 20px rgba(0, 0, 0, 0.1)",
              marginTop: "2px",
            },
            "& .MuiAutocomplete-listbox": {
              padding: "2px",
              "& .MuiAutocomplete-option": {
                fontSize: "0.8125rem",
                borderRadius: "6px",
                padding: "4px 10px",
                color: theme.palette.mode === "dark" ? "#e8eaed" : "#202124",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(138, 180, 248, 0.12)"
                      : "rgba(25, 103, 210, 0.08)",
                },
                "&[aria-selected='true']": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(138, 180, 248, 0.16)"
                      : "rgba(25, 103, 210, 0.12)",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138, 180, 248, 0.2)"
                        : "rgba(25, 103, 210, 0.16)",
                  },
                },
                "&.Mui-focused": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(138, 180, 248, 0.12)"
                      : "rgba(25, 103, 210, 0.08)",
                },
              },
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{ ...params.inputProps, maxLength: 18 }}
          inputRef={inputRef}
          value={value || ""}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: "26px",
              fontSize: "0.8125rem",
              fontWeight: 500,
              borderRadius: "13px",
              paddingLeft: "12px !important",
              paddingRight: "12px !important",
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              display: "flex",
              alignItems: "center",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(138, 180, 248, 0.16)"
                  : "rgba(25, 103, 210, 0.12)",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(138, 180, 248, 0.35)"
                  : "1px solid rgba(25, 103, 210, 0.25)",
              color: theme.palette.mode === "dark" ? "#9dc4ff" : "#1967d2",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 8px rgba(138, 180, 248, 0.25)"
                  : "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(138, 180, 248, 0.24)"
                    : "rgba(25, 103, 210, 0.18)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 0 12px rgba(138, 180, 248, 0.35)"
                    : "none",
              },
              "&.Mui-focused": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(138, 180, 248, 0.24)"
                    : "rgba(25, 103, 210, 0.18)",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(138, 180, 248, 0.5)"
                    : "rgba(25, 103, 210, 0.45)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 0 12px rgba(138, 180, 248, 0.35)"
                    : "none",
              },
            },
            "& .MuiInput-root": {
              "&:before, &:after": {
                display: "none",
              },
            },
            "& .MuiInputBase-input": {
              padding: "0 !important",
              textAlign: "center",
            },
          }}
          size="small"
          variant={"standard"}
          autoFocus
          onBlur={() => handleCaption()}
          onKeyDown={handleKey}
          onChange={handleTextChange}
        />
      )}
    />
  );
}

type CaptionButtonProps = {
  storyId: string;
  initialValue: string;
  onSelected?: (value?: string) => void;
  color?:
    | "inherit"
    | "action"
    | "disabled"
    | "error"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning";
  icon?: any;
};

function CaptionButton({
  storyId,
  initialValue,
  onSelected,
  color,
  icon,
}: CaptionButtonProps) {
  const theme = useTheme();
  const [input, setInput] = useState(false);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleSelected = (value?: string) => {
    setInput(false);
    if (value !== undefined) {
      setValue(value);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        minWidth: "180px",
      }}
    >
      {!input && !value && (
        <IconButton
          size="small"
          onClick={() => {
            setInput(true);
          }}
          sx={{
            height: "26px",
            width: "26px",
            padding: 0,
            color:
              theme.palette.mode === "dark"
                ? "rgba(138, 180, 248, 0.65)"
                : "rgba(25, 103, 210, 0.6)",
            "&:hover": {
              color:
                theme.palette.mode === "dark"
                  ? "#9dc4ff"
                  : "rgba(25, 103, 210, 0.9)",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(138, 180, 248, 0.08)"
                  : "rgba(25, 103, 210, 0.06)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 8px rgba(138, 180, 248, 0.3)"
                  : "none",
            },
          }}
        >
          {icon ? icon : <MoreHorizIcon fontSize="small" />}
        </IconButton>
      )}

      {!input && value && (
        <Chip
          onClick={() => {
            setInput(true);
          }}
          size="small"
          disabled={false}
          clickable={false}
          sx={{
            background:
              theme.palette.mode === "dark"
                ? "rgba(138, 180, 248, 0.16)"
                : "rgba(25, 103, 210, 0.12)",
            color: theme.palette.mode === "dark" ? "#9dc4ff" : "#1967d2",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(138, 180, 248, 0.35)"
                : "1px solid rgba(25, 103, 210, 0.25)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 0 8px rgba(138, 180, 248, 0.25)"
                : "none",
            width: "180px",
            height: "26px",
            fontWeight: 500,
            fontSize: "0.8125rem",
            cursor: "pointer",
            "& .MuiChip-label": {
              paddingLeft: "12px",
              paddingRight: "12px",
            },
            "&:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(138, 180, 248, 0.24)"
                  : "rgba(25, 103, 210, 0.18)",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(138, 180, 248, 0.45)"
                  : "rgba(25, 103, 210, 0.35)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 12px rgba(138, 180, 248, 0.35)"
                  : "none",
            },
          }}
          label={value}
        />
      )}

      {input && (
        <CaptionButtonInput
          storyId={storyId}
          initialValue={value}
          onSelected={handleSelected}
        />
      )}
    </Box>
  );
}

export function StoryCaption({
  item,
  initialValue,
}: {
  item: { SK: string };
  initialValue: string;
}) {
  return <CaptionButton initialValue={initialValue} storyId={item.SK} />;
}
